import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  LinearProgress,
  Link,
  Stack,
  Typography
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { fetchGraphQL } from "../../Graphql/utils";
import AlertBannerContext from "../../Contexts/alertBannerContext";
import "./styles/applicationDetail.css";
import {
  Application,
  getApplicationByIdResponse
} from "../../Graphql/types/application";
import {
  ApplicationInfoConfig,
  ApplicationJobInfoConfig,
  ApplicationMemberInfoConfig
} from "./config/applicationDetailConfig";
import { AdminGetApplicationByIdQuery } from "../../Graphql/querries/applicationQuery";
import { EMPLOYEE_PORTAL, JOBS } from "../Routes";
import ApplicationActions from "./ApplicationActions";
import WithAuth from "../../Hoc/WithAuth";
import {
  Employee,
  GetEmployeeInfoResponse
} from "../../Graphql/types/employee";
import { getEmployeeByIdQuery } from "../../Graphql/querries/employeeQuery";

const ApplicationDetail = () => {
  const navigate = useNavigate();
  const containerStyle = useMemo(
    () => ({ width: "100%", height: "calc(100vh - 100px)" }),
    []
  );
  // Extract the 'id' from the URL parameters
  const { applicationId } = useParams<{ applicationId: string }>();
  const { setAlert } = useContext(AlertBannerContext);
  const [applicationDetail, setApplicationDetail] = useState<
    Application | undefined
  >();
  const [employeeDetail, setEmployeeDetail] = useState<Employee>(
    {} as Employee
  );

  const { mutate: getEmployeeInfo, isPending: isFetchingEmployeeInfo } =
    useMutation<GetEmployeeInfoResponse, Error, string>({
      mutationKey: ["getEmployeeDetail"],
      mutationFn: (id) => fetchGraphQL(getEmployeeByIdQuery, { id }, setAlert),
      onSuccess: (response) => {
        const data = response.getEmployeeById;
        setEmployeeDetail(data);
      }
    });

  const { isPending, mutate } = useMutation<getApplicationByIdResponse>({
    mutationKey: ["getJobById"],
    mutationFn: () =>
      fetchGraphQL(AdminGetApplicationByIdQuery, { applicationId }, setAlert),
    onSuccess: (response) => {
      setApplicationDetail(response.adminGetApplicationById);
      getEmployeeInfo(response.adminGetApplicationById.employeeId);
    }
  });

  const getValueByKey = (obj: object, key: string): string | undefined => {
    return key.split(".").reduce<string | undefined>(
      (acc, part) => {
        if (acc && typeof acc === "object" && part in acc) {
          return (acc as Record<string, unknown>)[part] as string;
        }
        return undefined;
      },
      obj as unknown as string | undefined
    );
  };

  useEffect(() => {
    mutate();
  }, []);

  if (isPending || isFetchingEmployeeInfo) {
    return (
      <Stack width="100%" paddingBottom="1em" alignSelf="center">
        <LinearProgress />
      </Stack>
    );
  }

  if (!applicationDetail) {
    return <Typography>Application doesn&apos;t exist</Typography>;
  }

  return (
    <section style={containerStyle}>
      <Stack
        style={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "row",
          width: "100%",
          padding: "1em"
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Application Detail
        </Typography>
      </Stack>
      <Stack
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          width: "100%",
          padding: "2em",
          boxSizing: "border-box"
        }}
      >
        <Button
          onClick={() => navigate(-1)}
          variant="text"
          startIcon={<ArrowBackIcon />}
        >
          back
        </Button>
        {applicationDetail && (
          <ApplicationActions
            handleActionSuccess={mutate}
            application={applicationDetail}
          />
        )}
      </Stack>
      <Stack
        style={{
          background: "white",
          paddingBottom: "2em",
          gap: "1em",
          display: "flex",
          width: "100%",
          boxSizing: "border-box",
          flexDirection: "column"
        }}
      >
        <Stack
          display="flex"
          flexDirection="column"
          width="100%"
          gap={3}
          fontWeight="bold"
          padding="2em"
          boxSizing="border-box"
        >
          <Typography variant="h6">Application Info</Typography>
          <Box
            sx={{
              width: "100%",
              height: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap"
            }}
          >
            {ApplicationInfoConfig.map((config) => (
              <Stack
                key={config.key}
                style={{
                  border: "solid 0.5px grey",
                  padding: "1em",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1em",
                  width: "50%"
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {`${config.displayTitle}: `}
                </Typography>
                {config.key.includes("Url") &&
                getValueByKey(applicationDetail, config.key) ? (
                  <Link
                    href={getValueByKey(applicationDetail, config.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download or view in browser
                  </Link>
                ) : (
                  <Typography variant="body2">
                    {getValueByKey(applicationDetail, config.key) || "N/A"}
                  </Typography>
                )}
              </Stack>
            ))}
          </Box>
        </Stack>

        <Stack
          display="flex"
          flexDirection="column"
          width="100%"
          gap={3}
          fontWeight="bold"
          padding="2em"
          boxSizing="border-box"
        >
          <Typography variant="h6">Member Info</Typography>
          <Box
            sx={{
              width: "100%",
              height: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap"
            }}
          >
            {ApplicationMemberInfoConfig.map((config) => (
              <Stack
                key={config.key}
                style={{
                  border: "solid 0.5px grey",
                  padding: "1em",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1em",
                  width: "50%"
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {`${config.displayTitle}: `}
                </Typography>
                {config.key.includes("Url") &&
                (getValueByKey(applicationDetail, config.key) ||
                  getValueByKey(employeeDetail, config.key)) ? (
                  <Link
                    href={
                      config.key === "resumeUrl"
                        ? getValueByKey(applicationDetail, config.key) || "N/A"
                        : getValueByKey(employeeDetail, config.key) || "N/A"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Click to download or view in browser
                  </Link>
                ) : (
                  <Typography variant="body2">
                    {config.key === "id" &&
                    getValueByKey(employeeDetail, config.key) ? (
                      <Link
                        href={`${EMPLOYEE_PORTAL}/${getValueByKey(employeeDetail, config.key)}`}
                      >
                        {getValueByKey(employeeDetail, config.key)}
                      </Link>
                    ) : (
                      getValueByKey(employeeDetail, config.key) || "N/A"
                    )}
                  </Typography>
                )}
              </Stack>
            ))}
          </Box>
        </Stack>

        <Stack
          display="flex"
          flexDirection="column"
          width="100%"
          gap={3}
          fontWeight="bold"
          padding="2em"
          boxSizing="border-box"
        >
          <Typography variant="h6">Job Info</Typography>
          <Box
            sx={{
              width: "100%",
              height: "auto",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap"
            }}
          >
            {ApplicationJobInfoConfig.map((config) => (
              <Stack
                key={config.key}
                style={{
                  border: "solid 0.5px grey",
                  padding: "1em",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "start",
                  alignItems: "center",
                  gap: "1em",
                  width: "50%"
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  {`${config.displayTitle}: `}
                </Typography>
                {config.key.includes("Url") &&
                getValueByKey(applicationDetail, config.key) ? (
                  <Link
                    href={getValueByKey(applicationDetail, config.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Click to download or view in browser
                  </Link>
                ) : (
                  <div>
                    {config.key === "jobId" &&
                    getValueByKey(applicationDetail, config.key) ? (
                      <Link
                        href={`${JOBS}/${getValueByKey(applicationDetail, config.key)}`}
                      >
                        {getValueByKey(applicationDetail, config.key)}
                      </Link>
                    ) : (
                      <Typography variant="body2">
                        {getValueByKey(applicationDetail, config.key) || "N/A"}
                      </Typography>
                    )}
                  </div>
                )}
              </Stack>
            ))}
          </Box>
        </Stack>
      </Stack>
    </section>
  );
};

export default WithAuth(ApplicationDetail);
